import { Alert, Badge, Button, Module, ModuleBody, ModuleHead, PageFrame } from "@vjal/ui";
import { DataTableCollection } from "@vjal/ui";
import type { DataTableColumn } from "@vjal/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  adminCoachWorkflowFixture,
  getInviteWorkflowState
} from "../domain/admin-coach-workflows";
import { evaluateActionCapability } from "../domain/capabilities";
import {
  isLiveCoachingDataMode,
  useLiveCohortParticipants,
  useLiveCoachingRecord
} from "../hooks/useLiveProductData";
import { coachingQueryKeys } from "../hooks/coaching-query-keys";
import { useSessionWorkflowMutations } from "../hooks/useSessionWorkflowMutations";
import { humanizeStatus } from "../lib/status-format";
import { LiveListStatus } from "../components/LiveDataState";
import { useCoachingInviteLog } from "../hooks/useCoachingZ6Data";
import { Z6BindingStatus } from "./product-z6-shared";
import { liveRecordIdFromPath } from "./product-live-rows";
import { describeSessionWriteError } from "./sessions/sessions-helpers";
import type { CoachingInviteLogEntry } from "../services/coaching-z6-dtos";
import type { CoachingOperationResult } from "../services/coaching-dtos";
import type { ProductScreenContract, ProductScreenContext, TableRow } from "./product-route-contracts";

const activeSession = adminCoachWorkflowFixture.sessions[0];
const inviteState = getInviteWorkflowState(activeSession);

export function sessionInvitesScreen(): ProductScreenContract {
  const rows = isLiveCoachingDataMode() ? [] : fixtureInviteRows();

  return {
    title: () => "Invite operations",
    primaryJob: "Review invite recipients and separate database readiness evidence from provider delivery state.",
    secondaryJob: "Send, resend, and force-resend controls stay role-gated and idempotent.",
    workTitle: "Invite recipient review",
    rows,
    columns: inviteColumns(),
    states: ["review", "blocked-no-recipients", "readiness-evidence", "provider-closed", "event-history-recorded", "partial-failure", "support-ref"],
    apiFamilies: ["session-service", "history-service"],
    roleDeltas: "Operational writers can request invites; read-only roles see status only.",
    extensionSlots: ["Microsoft 365 delivery", "force resend", "recipient-level history"],
    mobilePosture: "Recipient list scrolls after provider status and before send controls.",
    evidenceRequirement: "Playwright must prove provider-closed labeling, recipient review, and send controls.",
    surfaceMode: "focused",
    customBody: InviteOperationsSurface
  };
}

function InviteOperationsSurface(context: ProductScreenContext) {
  return isLiveCoachingDataMode() ? <LiveInviteOperationsSurface {...context} /> : <MockInviteOperationsSurface />;
}

function MockInviteOperationsSurface() {
  const rows = fixtureInviteRows();

  return (
    <PageFrame title="Session invites" titleAs={2} subtitle="Delivery status and recipient review before send or force resend." className="col-span-12">
      <section aria-label="Session invites" className="flex flex-col gap-4" data-testid="session-invites-page">
        <Module span={12}>
          <ModuleHead title="Delivery status" subtitle="Invite records update immediately; Microsoft 365 calendar delivery can take longer." />
          <ModuleBody className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-center">
            <div className="space-y-3">
              <Alert variant="warning">{inviteState.providerLabel}</Alert>
              <p className="m-0 text-sm text-ink-muted">{inviteState.dbEvidenceLabel}</p>
              <Badge variant="warning">{inviteState.state}</Badge>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button type="button">Send Invites</Button>
              <Button type="button" variant="secondary">Force resend</Button>
            </div>
          </ModuleBody>
        </Module>
        <Module span={12}>
          <ModuleHead title="Recipient review" subtitle="Visible recipients must be reviewed before send or force resend." />
          <ModuleBody flush>
            <DataTableCollection caption="Invite recipient review" columns={inviteColumns()} data={rows} stableChipNames />
          </ModuleBody>
        </Module>
      </section>
    </PageFrame>
  );
}

function LiveInviteOperationsSurface({ role, routePath }: ProductScreenContext) {
  const queryClient = useQueryClient();
  const sessionId = liveRecordIdFromPath(routePath, "sessions");
  const session = useLiveCoachingRecord("sessions.detail", sessionId);
  const participants = useLiveCohortParticipants(session.record?.cohort_id ?? null);
  const inviteLog = useCoachingInviteLog(session.record ? sessionId : null);
  const mutations = useSessionWorkflowMutations();
  const sendDecision = evaluateActionCapability(role, "sessions:send-invites");
  const [outcome, setOutcome] = useState<{ action: "send" | "resend"; result: CoachingOperationResult } | null>(null);

  const inviteLogReady = Boolean(inviteLog.data && !inviteLog.isLoading && !inviteLog.isError && !inviteLog.denied);
  const rows = inviteLogReady ? liveInviteRows(participants.items, inviteLog.data?.inviteLog.entries ?? []) : [];
  const dataReady = Boolean(
    session.record &&
      !participants.isLoading &&
      !participants.isError &&
      !participants.denied &&
      !participants.isEmpty &&
      inviteLogReady
  );
  const actionsDisabled = !sendDecision.allowed || !sessionId || !dataReady;
  const pendingLabel = mutations.sendInvites.isPending
    ? "Sending invite request…"
    : mutations.forceResend.isPending
      ? "Submitting force-resend request…"
      : null;
  const mutationError = mutations.sendInvites.error ?? mutations.forceResend.error;
  const errorView = describeSessionWriteError(mutationError);

  async function runInviteAction(action: "send" | "resend") {
    if (!sessionId || actionsDisabled) return;
    mutations.sendInvites.reset();
    mutations.forceResend.reset();
    setOutcome(null);

    try {
      const result = action === "send"
        ? await mutations.sendInvites.mutateAsync({ sessionId, body: {} })
        : await mutations.forceResend.mutateAsync({ sessionId, body: { force: true } });
      setOutcome({ action, result });
      if (result.ok) {
        await queryClient.invalidateQueries({ queryKey: coachingQueryKeys.z6.inviteLog(sessionId) });
      }
    } catch {
      // The mutation's error is rendered below; no success state is implied.
    }
  }

  const sessionLabel = session.record?.title ?? (sessionId ? `Session ${sessionId}` : "Live session");
  const cohortLabel = session.record?.cohort_name ?? session.record?.cohort_id ?? "cohort unavailable";
  const summary = liveDeliverySummary(session.record?.invite_status, inviteLog.data?.inviteLog.entries ?? [], inviteLog.isLoading, inviteLog.isError || inviteLog.denied);

  return (
    <PageFrame title="Session invites" titleAs={2} subtitle="Delivery status and recipient review before send or force resend." className="col-span-12">
      <section aria-label="Session invites" className="flex flex-col gap-4" data-testid="session-invites-page">
        <Module span={12}>
          <ModuleHead title="Delivery status" subtitle={`${sessionLabel} · cohort ${cohortLabel}`} />
          <ModuleBody className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-center">
            <div className="space-y-3">
              {!sessionId ? <Alert variant="info">No live session id was found in this route.</Alert> : null}
              <LiveListStatus binding={session} label="session detail" />
              <Z6BindingStatus binding={inviteLog} label="invite trigger log" />
              <Alert variant={summary.providerClosed ? "warning" : "info"}>{summary.providerLabel}</Alert>
              <p className="m-0 text-sm text-ink-muted">{summary.dbEvidenceLabel}</p>
              <Badge variant={summary.providerClosed ? "warning" : "info"}>{summary.stateLabel}</Badge>
            </div>
            <div className="flex flex-wrap gap-3">
              {sendDecision.allowed ? (
                <>
                  <Button
                    type="button"
                    loading={mutations.sendInvites.isPending}
                    disabled={actionsDisabled || Boolean(pendingLabel)}
                    onClick={() => void runInviteAction("send")}
                  >
                    Send Invites
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    loading={mutations.forceResend.isPending}
                    disabled={actionsDisabled || Boolean(pendingLabel)}
                    onClick={() => void runInviteAction("resend")}
                  >
                    Force resend
                  </Button>
                </>
              ) : null}
            </div>
          </ModuleBody>
        </Module>
        <Module span={12}>
          <ModuleHead title="Recipient review" subtitle="Recipients and invite outcomes come from the live session cohort." />
          <ModuleBody className="space-y-4" flush>
            <LiveListStatus binding={participants} label="cohort recipients" />
            {inviteLogReady && !participants.isLoading && participants.isLiveBound && !participants.isError && !participants.denied && !participants.isEmpty && rows.length === 0 ? (
              <Alert variant="info">No invite statuses are recorded for the live recipients yet.</Alert>
            ) : null}
            {rows.length > 0 ? <DataTableCollection caption="Invite recipient review" columns={inviteColumns()} data={rows} stableChipNames /> : null}
          </ModuleBody>
        </Module>
        {pendingLabel ? <Alert variant="info">{pendingLabel}</Alert> : null}
        {errorView ? <Alert variant={errorView.tone === "error" ? "error" : "warning"}>{errorView.message}</Alert> : null}
        {outcome ? <InviteOutcome action={outcome.action} result={outcome.result} /> : null}
        {!sendDecision.allowed ? <Alert variant="info">{sendDecision.reason}</Alert> : null}
      </section>
    </PageFrame>
  );
}
function InviteOutcome({ action, result }: { action: "send" | "resend"; result: CoachingOperationResult }) {
  const actionLabel = action === "send" ? "Send invites" : "Force resend";
  const operationInvite = result.operationState?.invite;
  const providerMode = result.inviteLane?.providerMode;
  const operationProviderGate = operationInvite && typeof operationInvite === "object" && "providerGate" in operationInvite
    ? String(operationInvite.providerGate ?? "")
    : "";
  const deliveryGate = result.inviteLane?.deliveryGate ?? operationProviderGate;
  const zoomGate = result.inviteLane?.zoomGate;
  const readinessOnly = result.status === "provider_closed" || providerMode === "dry_run" || (providerMode !== "live" && (deliveryGate === "closed" || zoomGate === "closed"));
  const deliveryCopy = providerMode === "live"
    ? `Provider mode is live${deliveryGate ? `; delivery gate: ${humanizeStatus(deliveryGate)}` : ""}. Final delivery remains represented by provider outcomes.`
    : providerMode === "dry_run"
      ? "Provider mode is dry run; this request is readiness evidence only."
      : readinessOnly
        ? "Provider delivery is closed; this request is readiness evidence only."
        : "Provider delivery posture was not returned; no delivery claim is made.";
  const status = result.status ? ` Status: ${humanizeStatus(result.status)}.` : " The API did not return an operation status.";
  const reference = result.reference ? ` Reference: ${result.reference}.` : "";
  const message = result.ok
    ? `${actionLabel} API response confirmed.${status} ${deliveryCopy}${reference}`
    : `${actionLabel} API response did not confirm the request.${status}${reference}`;
  return <Alert variant={result.ok ? (readinessOnly ? "warning" : "success") : "warning"}>{message}</Alert>;
}

function fixtureInviteRows(): TableRow[] {
  return adminCoachWorkflowFixture.participants
    .filter((participant) => participant.cohortId === activeSession.cohortId)
    .map((participant) => ({
      id: participant.id,
      learner: participant.name,
      email: participant.email,
      readiness: inviteState.dbEvidenceLabel,
      provider: inviteState.providerLabel
    }));
}

function liveInviteRows(
  participants: Array<{ id: string; name?: string; email?: string }>,
  entries: CoachingInviteLogEntry[]
): TableRow[] {
  return participants.map((participant) => {
    const latest = latestInviteEntry(entries, participant.id);
    const calendar = latestInviteEntry(entries, participant.id, "calendar");
    return {
      id: participant.id,
      learner: participant.name ?? participant.email ?? `id ${participant.id}`,
      email: participant.email ?? "email unavailable",
      readiness: latest ? humanizeStatus(latest.outcome) : "No invite attempt recorded",
      provider: calendar ? humanizeStatus(calendar.outcome) : "No calendar attempt recorded"
    };
  });
}

function latestInviteEntry(entries: CoachingInviteLogEntry[], participantId: string, channel?: string) {
  return entries
    .filter((entry) => entry.participantId === participantId && (!channel || entry.channel === channel))
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt) || right.attempt - left.attempt)[0];
}

function liveDeliverySummary(inviteStatus: string | undefined, entries: CoachingInviteLogEntry[], loading: boolean, unavailable: boolean) {
  if (loading) {
    return {
      providerClosed: false,
      providerLabel: "Loading live invite delivery state…",
      dbEvidenceLabel: "The live invite trigger log is loading.",
      stateLabel: "Loading"
    };
  }
  if (unavailable) {
    return {
      providerClosed: false,
      providerLabel: "Live invite delivery state is unavailable; no provider outcome is assumed.",
      dbEvidenceLabel: "The invite trigger log did not provide a settled status.",
      stateLabel: "Unavailable"
    };
  }
  const latest = entries[0];
  const providerClosed = inviteStatus === "provider_closed";
  return {
    providerClosed,
    providerLabel: providerClosed
      ? "Provider delivery is closed; invite requests are recorded as readiness evidence only."
      : latest
        ? `Latest ${humanizeStatus(latest.channel)} invite outcome: ${humanizeStatus(latest.outcome)}. Provider delivery is not confirmed by this read.`
        : "No invite attempt has been recorded for this session yet.",
    dbEvidenceLabel: latest
      ? `Invite trigger log recorded at ${latest.createdAt}.`
      : "The live invite trigger log has no entries yet.",
    stateLabel: inviteStatus ? humanizeStatus(inviteStatus) : latest ? humanizeStatus(latest.outcome) : "Not requested"
  };
}

function inviteColumns(): DataTableColumn<TableRow>[] {
  return [
    { key: "learner", header: "Learner" },
    { key: "email", header: "Email" },
    { key: "readiness", header: "Invite status" },
    { key: "provider", header: "Calendar delivery" }
  ];
}
